import { useMemo, useState } from 'react';
import type { ImportRowView } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { useResourceList } from '#client/hooks/useResourceList';
import { formatCount } from '#client/lib/format';
import { RESOURCE } from '#client/lib/routes';
import { AliasResolver } from '../AliasResolver';
import type { ImportRunState } from '../useImportRun';
import { CheckRowsCard } from './CheckRowsCard';
import { CheckSummaryCard } from './CheckSummaryCard';
import { ENDPOINT } from '#client/lib/endpoints';

/**
 * Step three: what the file will actually do, before it does it.
 *
 * The loop the operator lives in. Re-checking is free and changes nothing
 * outside the staging tables, so the honest thing is to make it obvious that
 * they can go back, change the mapping and look again.
 */
export function CheckStep({ state }: { state: ImportRunState }) {
  const { structure, run, summary, isBusy, validate } = state;
  const [status, setStatus] = useState<string>('');

  const extraQuery = useMemo(() => (status ? { status } : {}), [status]);
  const rows = useResourceList<ImportRowView>({
    path: run ? ENDPOINT.importRows(run.id) : RESOURCE.imports,
    limit: 25,
    extraQuery,
  });

  if (!structure || !run) return null;

  /**
   * Check, then re-read the rows.
   *
   * Both halves, always. `validate` rewrites every staged row in place, so a
   * summary that says "10 ready" beside a table still showing "Not checked" is
   * not a stale cache — it is two views of the same run disagreeing, which is
   * the one thing this screen exists to avoid.
   */
  const handleCheck = () => void validate().then(() => rows.refresh());

  if (summary === null) {
    return (
      <div className="c-form-stack">
        <Card title="Check the file" subtitle="Nothing is written until you say so">
          <p>
            This reads every row against the mapping you chose and reports what it will do. It
            changes nothing, and you can go back and re-map as many times as you like.
          </p>
          <Button variant="primary" onClick={handleCheck} loading={isBusy}>
            Check {formatCount(run.total_rows)} rows
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="c-form-stack">
      {summary.warnings.map((warning) => (
        <Banner key={warning} tone="warning">
          {warning}
        </Banner>
      ))}

      {summary.date_order_ambiguous && (
        <Banner tone="warning" title="Check the date order">
          Every date in this file has a day of 12 or less, so day/month and month/day both read as
          valid dates and nothing in the file says which was meant. The preview shows them written
          out — go back and change the date order if they look wrong.
        </Banner>
      )}

      <CheckSummaryCard summary={summary} />

      <AliasResolver unresolved={summary.unresolved} onResolved={handleCheck} />

      <CheckRowsCard
        run={run}
        rows={rows}
        status={status}
        isChecking={isBusy}
        onStatusChange={(next) => {
          setStatus(next);
          rows.setPage(1);
        }}
        onCheck={handleCheck}
      />
    </div>
  );
}
